import {
  Button,
  Checkbox,
  InlineNotification,
  NumberInput,
  Search,
  Select,
  SelectItem,
  SkeletonText,
  TextInput,
} from '@carbon/react';
import { Add, TrashCan } from '@carbon/icons-react';
import { localized, useTranslation } from '@pos/i18n';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { asProblem, problemMessage } from '../problems';
import { fold, ordersKey, useFormat, useOrdersApi, type LineDraft } from './model';
import type { MenuItem } from './ordersApi';

/** The priced menu for a sale type: search, pick an item, choose its options, add it to the drafts. */
export function ItemPicker({ saleTypeId, onAdd }: { saleTypeId: string; onAdd(draft: LineDraft): void }) {
  const { t } = useTranslation();
  const api = useOrdersApi();
  const format = useFormat();
  const [search, setSearch] = useState('');
  const [chosen, setChosen] = useState<MenuItem>();
  const menu = useQuery({ queryKey: [...ordersKey, 'menu', saleTypeId], queryFn: () => api.menu(saleTypeId) });

  if (menu.error) return <InlineNotification kind="error" lowContrast hideCloseButton title={problemMessage(t, asProblem(menu.error))} />;
  if (!menu.data) return <SkeletonText paragraph lineCount={6} />;

  const currency = menu.data.currencyCode;
  const query = fold(search.trim());
  const items = menu.data.items.filter(
    (item) => !query || fold(localized(item.nameI18n, item.name)).includes(query) || fold(item.name).includes(query) || fold(item.code).includes(query),
  );

  return (
    <div className="pos-orders__picker">
      <Search
        labelText={t('orders.create.searchItems')}
        placeholder={t('orders.create.searchItems')}
        closeButtonLabelText={t('carbon.clearSearch')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <ul className="pos-orders__menu" aria-label={t('orders.create.menu')}>
        {items.map((item) => {
          const unavailable = item.price == null && !item.isOpenPrice && !item.units?.some((u) => u.price != null);
          return (
            <li key={item.id}>
              <button
                type="button"
                className={chosen?.id === item.id ? 'pos-orders__menu-item pos-orders__menu-item--active' : 'pos-orders__menu-item'}
                disabled={unavailable}
                onClick={() => setChosen(item)}
              >
                <span>{localized(item.nameI18n, item.name)}</span>
                <span className="pos-admin-page__number">
                  {item.price != null ? format.money(item.price, currency) : t('orders.create.noPrice')}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {chosen && (
        <ItemOptions
          key={chosen.id}
          item={chosen}
          currency={currency}
          onCancel={() => setChosen(undefined)}
          onAdd={(draft) => {
            onAdd(draft);
            setChosen(undefined);
          }}
        />
      )}
    </div>
  );
}

function ItemOptions({ item, currency, onAdd, onCancel }: { item: MenuItem; currency: string; onAdd(d: LineDraft): void; onCancel(): void }) {
  const { t } = useTranslation();
  const format = useFormat();
  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState('');
  // An item with several selling units asks for one, the default preselected (ADR 0030).
  const units = item.units ?? [];
  const [unitId, setUnitId] = useState(() => (units.find((u) => u.isDefaultSale) ?? units.find((u) => u.isBase) ?? units[0])?.uomId);
  const unit = units.find((u) => u.uomId === unitId);
  const price = unit ? unit.price : item.price;
  const [openPrice, setOpenPrice] = useState<number | null>(item.isOpenPrice ? Number(item.price ?? 0) : null);
  const [selected, setSelected] = useState<string[]>(() =>
    item.modifierGroups.flatMap((g) => g.options.filter((o) => o.isDefault).map((o) => o.id)),
  );

  const groupErrors = item.modifierGroups.map((group) => {
    const count = group.options.filter((o) => selected.includes(o.id)).length;
    const min = Number(group.minSelect);
    const max = group.maxSelect == null ? null : Number(group.maxSelect);
    if (count < min) return t('orders.create.min', { count: min });
    if (max != null && count > max) return t('orders.create.max', { count: max });
    return undefined;
  });
  const valid = groupErrors.every((e) => !e) && quantity > 0 && (price != null || item.isOpenPrice);
  const options = item.modifierGroups.flatMap((g) => g.options);

  return (
    <div className="pos-orders__options">
      <h3 className="pos-orders__options-title">{localized(item.nameI18n, item.name)}</h3>
      {item.modifierGroups.map((group, index) => (
        <fieldset key={group.id} className="pos-orders__group">
          <legend>{localized(group.nameI18n, group.name)}</legend>
          {group.options.map((option) => (
            <Checkbox
              key={option.id}
              id={`option-${item.id}-${option.id}`}
              labelText={
                Number(option.price) > 0
                  ? `${localized(option.labelI18n, option.label)} (+${format.money(option.price, currency)})`
                  : localized(option.labelI18n, option.label)
              }
              checked={selected.includes(option.id)}
              onChange={(_e, { checked }) =>
                setSelected((ids) => (checked ? [...ids, option.id] : ids.filter((id) => id !== option.id)))
              }
            />
          ))}
          {groupErrors[index] && <p className="pos-orders__error">{groupErrors[index]}</p>}
        </fieldset>
      ))}
      {units.length > 1 && (
        <Select id={`unit-${item.id}`} labelText={t('orders.create.unit')} value={unitId} onChange={(e) => setUnitId(e.target.value)}>
          {units.map((u) => (
            <SelectItem
              key={u.uomId}
              value={u.uomId}
              text={u.price != null ? `${u.name} · ${format.money(u.price, currency)}` : `${u.name} · ${t('orders.create.noPrice')}`}
            />
          ))}
        </Select>
      )}
      <div className="pos-orders__option-row">
        <NumberInput
          id={`quantity-${item.id}`}
          label={t('orders.lines.quantity')}
          min={1}
          max={9999}
          value={quantity}
          onChange={(_e, { value }) => setQuantity(Number(value) || 0)}
        />
        {item.isOpenPrice && (
          <NumberInput
            id={`price-${item.id}`}
            label={t('orders.create.openPrice')}
            min={0}
            value={openPrice ?? 0}
            onChange={(_e, { value }) => setOpenPrice(Number(value) || 0)}
            hideSteppers
          />
        )}
      </div>
      <TextInput id={`note-${item.id}`} labelText={t('orders.lines.note')} value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
      <div className="pos-orders__option-actions">
        <Button kind="secondary" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button
          renderIcon={Add}
          disabled={!valid}
          onClick={() =>
            onAdd({
              key: crypto.randomUUID(),
              itemId: item.id,
              name: localized(item.nameI18n, item.name),
              quantity,
              uomId: unit?.uomId ?? null,
              unitName: unit && !unit.isBase ? unit.name : null,
              modifierIds: selected,
              modifierLabels: options.filter((o) => selected.includes(o.id)).map((o) => localized(o.labelI18n, o.label)),
              note,
              openPrice: item.isOpenPrice ? openPrice : null,
              unitPrice: item.isOpenPrice ? openPrice : price == null ? null : Number(price),
              currency,
            })
          }
        >
          {t('orders.create.add')}
        </Button>
      </div>
    </div>
  );
}

/** The chosen items before saving: quantities and options, with list prices only. */
export function DraftLines({ drafts, onChange }: { drafts: LineDraft[]; onChange(drafts: LineDraft[]): void }) {
  const { t } = useTranslation();
  const format = useFormat();
  if (drafts.length === 0) return <p className="pos-admin-page__empty">{t('orders.create.emptyCart')}</p>;
  return (
    <ul className="pos-orders__drafts">
      {drafts.map((draft) => (
        <li key={draft.key} className="pos-orders__draft">
          <div>
            <p>
              <span className="pos-admin-page__number">{draft.quantity} ×</span> {draft.unitName ? `${draft.name} (${draft.unitName})` : draft.name}
            </p>
            {draft.modifierLabels.length > 0 && <p className="pos-orders__muted">{draft.modifierLabels.join(', ')}</p>}
            {draft.note && <p className="pos-orders__muted">{draft.note}</p>}
          </div>
          <span className="pos-admin-page__number">{draft.unitPrice != null ? format.money(draft.unitPrice, draft.currency) : ''}</span>
          <Button
            kind="ghost"
            hasIconOnly
            renderIcon={TrashCan}
            iconDescription={t('orders.actions.remove')}
            onClick={() => onChange(drafts.filter((d) => d.key !== draft.key))}
          />
        </li>
      ))}
    </ul>
  );
}
